import { isBefore, isSameDay } from 'date-fns';
import React, { useEffect, useMemo, useRef } from 'react';
import { IconFormIcons } from 'shared/assets/icons';
import { TaskMetaInteractions } from 'shared/components/connected/task-meta-interactions';
import { TaskDetailCheckbox } from 'shared/components/ui/task-detail-checkbox';
import { useToday } from 'shared/contexts/today';
import { useActiveSchedule } from 'shared/hooks/use-active-schedule';
import { Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { getScheduleFrequencyDayTarget } from 'shared/utils/get-schedule-frequency-day-target';
import { getTaskCheckOption } from 'shared/utils/get-task-check-option';
import { getTaskDayCompletionPercentage } from 'shared/utils/get-task-day-completion-percentage';
import { getTaskDueDate } from 'shared/utils/get-task-due';

import { useData } from '../../use-data';
import { Title } from '../title';
import * as Styled from './main.style';

export type TaskDetailMainProps = {
  task: Task;
  updateTask: (task: Partial<Task>) => void;
  onComplete: (date?: Timestamp) => void;
  onNameSubmit?: (task: Partial<Task>) => void;
};

export const Main: React.FC<TaskDetailMainProps> = ({
  task,
  updateTask,
  onComplete,
  onNameSubmit,
}) => {
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const today = useToday();
  const { weekStartsOn } = useData();

  const activeSchedule = useActiveSchedule(task.schedules, today);

  const preventPropagation = (e: React.MouseEvent) => e.stopPropagation();

  const onChangeName = (name: string) => {
    if (name) {
      updateTask({ name });
    }
  };

  const onSubmitName = onNameSubmit
    ? (name: string) => onNameSubmit({ name })
    : undefined;

  const completionPercentage = useMemo(
    () =>
      task.schedules?.length
        ? getTaskDayCompletionPercentage(task as Task, {
            day: today,
            weekStartsOn,
          })
        : undefined,
    [task, today, weekStartsOn],
  );

  const target = useMemo(() => {
    if (!activeSchedule) {
      return;
    }

    return activeSchedule.frequency
      ? getScheduleFrequencyDayTarget(activeSchedule)
      : activeSchedule.completionTarget.count;
  }, [activeSchedule]);

  const dateToRepresent = useMemo(() => {
    if (![TaskType.Habit, TaskType.Repeating].includes(task.type)) {
      return undefined;
    }

    return getTaskDueDate(task, { today, weekStartsOn }) ?? today;
  }, [task, today, weekStartsOn]);

  const overdueDate =
    dateToRepresent &&
    isBefore(dateToRepresent, today) &&
    !isSameDay(dateToRepresent, today)
      ? dateToRepresent
      : undefined;

  const checkOption = useMemo(
    () =>
      getTaskCheckOption(task, {
        day: today,
        today,
        weekStartsOn,
        showRepeatingAsTodo: true,
      }),
    [task, today, weekStartsOn],
  );

  useEffect(() => {
    if (!task.name) {
      titleRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should only run once
  }, []);

  return (
    <Styled.Container>
      <Styled.TitleBar>
        <Styled.CheckboxContainer onClick={preventPropagation}>
          <TaskDetailCheckbox
            checked={checkOption}
            name={task.name}
            type={task.type}
            icon={task.iconName ? IconFormIcons[task.iconName] : undefined}
            onChange={onComplete}
            completionPercentage={completionPercentage}
            target={target}
            priority={task.priority}
            isFrozen={!!(task as Task).frozenAt}
            overdueDate={overdueDate}
          />
        </Styled.CheckboxContainer>
        <Styled.TitleContainer>
          <Title
            ref={titleRef}
            value={task.name ?? ''}
            onChange={onChangeName}
            onSubmit={onSubmitName}
            isFrozen={!!(task as Task).frozenAt}
            completed={!!(task as Task).completedAt}
          />
          {!!dateToRepresent && (
            <TaskMetaInteractions
              task={task}
              displayDate={dateToRepresent}
              autoWidth
            />
          )}
        </Styled.TitleContainer>
      </Styled.TitleBar>
    </Styled.Container>
  );
};
